import { HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { inject, Injectable } from '@angular/core';
import { BaseService } from '../base-service';
import {
  AllGiftCardOfferCoupon,
  FidelityCard,
  FidelityCardTypeEnum,
  FidelityCardPointInfo,
  FidelityCardPointSimulationDto,
  GetCustomerFidelityInfoInput,
  GetCustomerFidelityInfoNouveauPointInput,
  PointInfo,
} from './fidelity';
import { UpdateCustomerComplete } from './update-customer-fidelity';
import { AddFidelityCardCustomer, InfoReturnCreateFidelityCard } from './create-fidelity-card';
import { GetPaymentLinkPw, PaytweakRequestInput } from './paytweak';
import { ColleagueStatistics, GetColleagueStatisticsInput } from './get-customer-statistics';
import { FeatureSettingsService } from '../feature-settings/feature-settings.service';

@Injectable({
  providedIn: 'root',
})
export class FidelityService extends BaseService {
  private readonly featureSettingsService = inject(FeatureSettingsService);

  public get baseApiUrl() {
    return environment.configurators2d.baseApi;
  }

  public get apiUrl() {
    return this.baseApiUrl + 'Fidelite/';
  }

  private async ensureFidelityActionEnabled(): Promise<void> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      throw new Error('La fonctionnalité fidélité est désactivée.');
    }
  }

  /**
   * Permet de récupérer les infos fidélité du customer sélectionné
   * @param input
   * @returns
   */
  public async getCustomerFidelityCardInfo(input: GetCustomerFidelityInfoInput): Promise<FidelityCardPointInfo> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return this.buildDisabledFidelityCardPointInfo(input);
    }

    const params = new HttpParams().set('idKingfisher', input.idKingfisher);
    return this.get<FidelityCardPointInfo>(this.apiUrl + 'FideliteCardInfoPoint', params);
  }

  /**
   * Permet de renouveler une carte payante expirée
   * @param input
   * @returns
   */
  public async postRenewPremiumCard(input: GetCustomerFidelityInfoInput): Promise<any> {
    await this.ensureFidelityActionEnabled();

    const params = new HttpParams()
      .set('emailUserConnect', this.current.vendor.email)
      .set('idKingfisher', input.idKingfisher);
    return this.post(this.apiUrl + 'RenewPremiumCard', input, params);
  }

  /**
   * Permet de calculer les nouveaux points de fidélité
   * @param input
   * @returns
   */
  public async getCustomerFidelityCardInfoNouveauPoint(input: GetCustomerFidelityInfoNouveauPointInput): Promise<FidelityCardPointSimulationDto> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return this.buildDisabledFidelityCardPointSimulation(input);
    }

    const params = new HttpParams()
      .set('idKingfisher', input.customerId)
      .set('montant', Math.floor(input.amount));
    const res = await this.get(this.apiUrl + 'GetNouveauPoint', params);
    return { ...res, amount: Math.floor(input.amount) };
  }

  /**
   * Permet de mettre à jour le customer coté fid (met à jour dans les serveurs de KF et Cheetah)
   * @param input
   * @returns
   */
  public async updateCustomerFidelity(input: UpdateCustomerComplete): Promise<void> {
    await this.ensureFidelityActionEnabled();

    const params = new HttpParams().set('emailUserConnect', this.current.vendor.email);
    return this.post(this.apiUrl + 'UpdateCustomer', input, params);
  }

  /**
   * Permet de créer uen carte de fidélité
   * @param input
   * @returns
   */
  public async createFidelityCard(input: AddFidelityCardCustomer): Promise<InfoReturnCreateFidelityCard> {
    await this.ensureFidelityActionEnabled();

    const params = new HttpParams().set('emailUserConnect', this.current.vendor.email);
    return this.post<InfoReturnCreateFidelityCard>(this.apiUrl + 'CreateFidelityCard', input, params);
  }

  /**
   * Permet de récupérer le lien de payment via paytweak
   * @param input
   * @returns
   */
  public async getPaymentLinkPaytweak(input: PaytweakRequestInput): Promise<GetPaymentLinkPw> {
    await this.ensureFidelityActionEnabled();

    const params = this.buildHttpParamsFromObject(input);
    return this.get<GetPaymentLinkPw>(this.apiUrl + 'PaymentLinkPaytweak', params);
  }

  /**
   * Permet de récupérer les statistiques fidélités du vendeur connecté
   * @param input
   * @returns
   */
  public async getColleagueStatistics(input: GetColleagueStatisticsInput): Promise<ColleagueStatistics> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return {
        startDate: new Date(input.startDate),
        endDate: new Date(input.endDate),
        freeCardsTotal: 0,
        premiumCardsTotal: 0,
      };
    }

    const params = this.buildHttpParamsFromObject(input);
    return this.post<ColleagueStatistics>(this.apiUrl + 'GetColleagueStatistics', {}, params);
  }

  private buildDisabledFidelityCardPointInfo(input: GetCustomerFidelityInfoInput): FidelityCardPointInfo {
    const fidelityCard = Object.assign(new FidelityCard(), {
      cardNumber: '',
      cardEnabled: false,
      creationDate: '',
      finalExpirationDate: '',
      type: FidelityCardTypeEnum.AUCUNE0,
      customerId: input.idKingfisher,
      memberId: '',
      captainWalletUserId: '',
      cardIsPaidButNotActiveYet: false,
    });
    const discountsList = Object.assign(new AllGiftCardOfferCoupon(), {
      giftCardsList: [],
      offerCouponsList: [],
    });
    const pointInfo = Object.assign(new PointInfo(), {
      cardNumber: '',
      numberOfPoint: 0,
      maxPointForNextStep: 0,
      customerId: input.idKingfisher,
      memberId: '',
      discountsList,
    });

    return {
      fidelityCard,
      pointInfo,
    };
  }

  private buildDisabledFidelityCardPointSimulation(input: GetCustomerFidelityInfoNouveauPointInput): FidelityCardPointSimulationDto {
    return new FidelityCardPointSimulationDto({
      type: FidelityCardTypeEnum.AUCUNE0,
      hasCard: false,
      cardNumber: '',
      freeCardEarnedPoints: 0,
      premiumCardEarnedPoints: 0,
      freeCardCoupons: [],
      premiumCardCoupons: [],
      amount: Math.floor(input.amount),
    });
  }
}
